import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Rating,
  Tabs,
  Tab,
  TextField,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert
} from '@mui/material';
import { Add, Remove, AddShoppingCart as AddShoppingCartIcon, Favorite as FavoriteIcon, FavoriteBorder as FavoriteBorderIcon } from '@mui/icons-material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useWishlist } from '../../context/WishlistContext';
import { useParams } from 'react-router-dom';
import { Product, Review } from '../../services/FirestoreService';
import {
  getProductById,
  getReviewsByProduct,
  addReview,
  hasUserPurchasedProduct
} from '../../services/FirestoreService';

const ProductDetail: React.FC = () => {
  const { dispatch } = useCart();
  const { currentUser } = useAuth();
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(5);
  const [loading, setLoading] = useState(true);
  const [recentlyViewed, setRecentlyViewed] = useState<Product[]>([]);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // State to track if user has purchased this product
  useEffect(() => {
    if (currentUser && product?.id) {
      const checkPurchase = async () => {
        const purchased = await hasUserPurchasedProduct(currentUser.uid, product.id);
        setHasPurchased(purchased);
      };
      checkPurchase();
    }
  }, [currentUser, product?.id]);

  // Fetch product and reviews
  useEffect(() => {
    const fetchProduct = async () => {
      if (id) {
        try {
          setLoading(true);
          const productData = await getProductById(id);
          if (productData) {
            setProduct(productData);
            
            // Set active image
            if (productData.images && productData.images.length > 0) {
              setActiveImage(productData.images[0]);
            } else if (productData.image) {
              setActiveImage(productData.image);
            }
            
            // Get product reviews
            const productReviews = await getReviewsByProduct(id);
            setReviews(productReviews);
          } else {
            setAlertMessage({ type: 'error', message: 'Product not found.' });
          }
        } catch (error) {
          console.error('Error fetching product:', error);
          setAlertMessage({ type: 'error', message: 'Error fetching product.' });
        } finally {
          setLoading(false);
        }
      }
    };

    fetchProduct();
  }, [id]);

  // Update recently viewed products in localStorage
  useEffect(() => {
    if (product) {
      const viewedProducts = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
      const updatedViewed = [product, ...viewedProducts.filter((p: Product) => p.id !== product.id)].slice(0, 4);
      localStorage.setItem('recentlyViewed', JSON.stringify(updatedViewed));
      setRecentlyViewed(updatedViewed);
    }
  }, [product]);

  const handleAddToCart = () => {
    if (!product) return;

    // Check if adding this quantity exceeds stock
    if (quantity > (product.stock || 0)) {
      alert(`Cannot add ${quantity} items. Only ${product.stock || 0} items available in stock.`);
      return;
    }

    dispatch({
      type: 'ADD_ITEM',
      payload: {
        id: product.id,
        name: product.name,
        price: product.price,
        discountedPrice: product.discountedPrice,
        quantity: quantity,
        image: product.image,
        stock: product.stock
      }
    });
  };

  const handleAddComment = async () => {
    if (!product || !comment.trim()) return;

    if (!currentUser) {
      setAlertMessage({ type: 'error', message: 'Please log in to add a review.' });
      return;
    }

    try {
      await addReview({
        productId: product.id,
        user: currentUser.uid,
        rating: rating,
        comment: comment.trim()
      });

      // Refresh reviews
      const updatedReviews = await getReviewsByProduct(product.id);
      setReviews(updatedReviews);
      setComment('');
      setRating(5);
      setAlertMessage({ type: 'success', message: 'Review added successfully!' });
    } catch (error) {
      console.error('Error adding review:', error);
      setAlertMessage({ type: 'error', message: 'Error adding review. Please try again.' });
    }
  };

  const toggleWishlist = async () => {
    if (!currentUser) {
      setAlertMessage({ type: 'error', message: 'Please log in to add to wishlist.' });
      return;
    }

    if (!product) return;

    try {
      if (isInWishlist(product.id)) {
        // Find the wishlist item and remove it
        const wishlistItems = JSON.parse(localStorage.getItem('wishlist') || '[]');
        const wishlistItem = wishlistItems.find((item: any) => item.productId === product.id);
        if (wishlistItem) {
          await removeFromWishlist(wishlistItem.id);
        }
      } else {
        await addToWishlist(product.id);
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      setAlertMessage({ type: 'error', message: 'Error updating wishlist. Please try again.' });
    }
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container>
        <Typography variant="h6" align="center" sx={{ my: 4 }}>
          Product not found
        </Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      {alertMessage && (
        <Alert 
          severity={alertMessage.type} 
          onClose={() => setAlertMessage(null)}
          sx={{ mb: 2 }}
        >
          {alertMessage.message}
        </Alert>
      )}

      <Grid container spacing={4} columns={{ xs: 12, md: 12 }}>

        <Grid component="div" size={{ xs: 12, md: 6 }}>
          <Card>
            <CardMedia
              component="img"
              height="500"
              image={activeImage}
              alt={product.name}
            />
          </Card>

          {/* Thumbnail Images */}
          {product.images && product.images.length > 1 && (
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              {product.images.map((img, index) => (
                <Card
                  key={index}
                  sx={{
                    cursor: 'pointer',
                    flex: 1,
                    border: activeImage === img ? '2px solid #1976d2' : '2px solid transparent'
                  }}
                  onClick={() => setActiveImage(img)}
                >
                  <CardMedia
                    component="img"
                    height="100"
                    image={img}
                    alt={`${product.name} ${index + 1}`}
                  />
                </Card>
              ))}
            </Box>
          )}
        </Grid>


        <Grid  component="div" size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h4" component="h1" gutterBottom>
                {product.name}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Rating
                  value={product.rating || 0}
                  readOnly
                  precision={0.5}
                />
                <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                  ({reviews.length} reviews)
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                {product.discountedPrice ? (
                  <>
                    <Typography variant="h5" color="error" sx={{ mr: 2 }}>
                      ₺{product.discountedPrice}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                      ₺{product.price}
                    </Typography>
                    <Chip
                      label={`${Math.round(((product.price - product.discountedPrice) / product.price) * 100)}%`}
                      color="error"
                      size="small"
                      sx={{ ml: 2 }}
                    />
                  </>
                ) : (
                  <Typography variant="h5" color="error">
                    ₺{product.price}
                  </Typography>
                )}
              </Box>

              <Typography variant="body1" paragraph>
                {product.description}
              </Typography>

              {product.features && product.features.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Features:
                  </Typography>
                  <ul>
                    {product.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Typography variant="body1" sx={{ mr: 2 }}>
                  Stock:
                </Typography>
                <Chip
                  label={`${product.stock} ${product.stock > 10 ? 'in Stock' : product.stock > 0 ? 'Low Stock' : 'Out of Stock'}`}
                  color={product.stock > 10 ? 'success' : product.stock > 0 ? 'warning' : 'error'}
                />
              </Box>

              {/* Quantity Selector */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Typography variant="body1" sx={{ mr: 2 }}>
                  Quantity:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                  >
                    <Remove />
                  </IconButton>
                  <TextField
                    value={quantity}
                    size="small"
                    variant="outlined"
                    inputProps={{ min: 1, max: product.stock }}
                    type="number"
                    sx={{ width: 80, mx: 1 }}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value) && value >= 1) {
                        setQuantity(Math.min(value, product.stock || value));
                      }
                    }}
                  />
                  <IconButton
                    onClick={() => setQuantity(Math.min(product.stock || quantity, quantity + 1))}
                    disabled={quantity >= (product.stock || 0)}
                  >
                    <Add />
                  </IconButton>
                </Box>
              </Box>

              {/* Add to Cart and Wishlist Buttons */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddShoppingCartIcon />}
                  onClick={handleAddToCart}
                  size="large"
                  sx={{ flex: 1 }}
                >
                  Sepete Ekle
                </Button>
                <IconButton
                  color={isInWishlist(product.id) ? "error" : "default"}
                  onClick={toggleWishlist}
                  disabled={!currentUser}
                  title={currentUser ? (isInWishlist(product.id) ? "Favorilerden Kaldır" : "Favorilere Ekle") : "Favorilere eklemek için giriş yapın"}
                >
                  {isInWishlist(product.id) ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                </IconButton>
              </Box>

              <Box sx={{ my: 2 }}>
                <Typography variant="h6">Etiketler:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  <Chip label="boya seti" size="small" variant="outlined" />
                  <Chip label="aile aktivitesi" size="small" variant="outlined" />
                  <Chip label="çocuk oyunları" size="small" variant="outlined" />
                  <Chip label="eğlenceli" size="small" variant="outlined" />
                  <Chip label="eğitici" size="small" variant="outlined" />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid  component="div" size={{ xs: 12 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label="Açıklama" />
              <Tab label="Yorumlar" />
              <Tab label="Ek Bilgiler" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          {activeTab === 0 && (
            <Box sx={{ p: 3, mt: 2 }}>
              <Typography variant="body1">
                {product.description || 'Product description not available.'}
              </Typography>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ p: 3, mt: 2 }}>
              {/* Add Review */}
              {currentUser && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Yorum Ekle
                    </Typography>
                    <Rating
                      value={rating}
                      onChange={(event, newValue) => {
                        setRating(newValue || 5);
                      }}
                    />
                    <TextField
                      label="Yorumunuz"
                      multiline
                      rows={4}
                      fullWidth
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      sx={{ mt: 2 }}
                    />
                    <Button
                      variant="contained"
                      sx={{ mt: 2 }}
                      onClick={handleAddComment}
                      disabled={!comment.trim()}
                    >
                      Yorumu Gönder
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Reviews List */}
              <Typography variant="h6" gutterBottom>
                Yorumlar ({reviews.length})
              </Typography>
              {reviews.length > 0 ? (
                reviews.map((review) => (
                  <Card key={review.id} sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Rating value={review.rating} readOnly size="small" />
                        <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                          {new Date(review.date || new Date()).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Typography variant="body1">
                        {review.comment}
                      </Typography>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Typography>Henüz yorum yapılmamış.</Typography>
              )}
            </Box>
          )}

          {activeTab === 2 && (
            <Box sx={{ p: 3, mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Ek Bilgiler
              </Typography>
              <Typography variant="body1">
                {product.features ? product.features.join(', ') : 'Additional information not available.'}
              </Typography>
            </Box>
          )}
        </Grid>


        {recentlyViewed.length > 0 && (
          <Grid component="div" size={{ xs: 12 }} mt={3}>
            <Typography variant="h5" gutterBottom>
              Son Gördükleriniz
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto' }}>
              {recentlyViewed.map((item) => (
                <Card
                  key={item.id}
                  sx={{ minWidth: 150, cursor: 'pointer', flex: '0 0 auto' }}
                  onClick={() => window.location.href = `/product/${item.id}`}
                >
                  <CardMedia
                    component="img"
                    height="100"
                    image={item.image}
                    alt={item.name}
                  />
                  <CardContent>
                    <Typography variant="body2" noWrap>
                      {item.name}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Grid>
        )}
      </Grid>
    </Container>

  )};
export default ProductDetail;